import React, { useState, useRef } from 'react';
import { Gift, Copy, Users, Package, Award, User, ShoppingBag, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useRewards } from '../hooks/useRewards';

import { RewardsClaim, Referral } from '@shared/sdk-v1';
import { useReferral } from '../hooks/useReferral';
import { Button } from '@shared/components';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import { useTrackEvent } from '../utils/track';
import { getRewardTransactionDisplay } from '../utils/rewardTransactions';

const TRANSACTION_TONE_STYLES = {
  referral: {
    wrapper: 'bg-purple-100 dark:bg-purple-900/30',
    icon: 'text-purple-600 dark:text-purple-400',
  },
  package: {
    wrapper: 'bg-green-100 dark:bg-green-900/30',
    icon: 'text-green-600 dark:text-green-400',
  },
  redemption: {
    wrapper: 'bg-blue-100 dark:bg-blue-900/30',
    icon: 'text-blue-600 dark:text-primary',
  },
} as const;

const TRANSACTION_TONE_ICONS = {
  referral: Users,
  package: Package,
  redemption: Gift,
} as const;

const Referrals = () => {
  const track = useTrackEvent();
  const [activeTab, setActiveTab] = useState('earn');
  const [showRewardsModal, setShowRewardsModal] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const {
    rewardsAccount,
    availableRewards,
    transactions,
    claims,
    rewardSetting,
    isLoading: rewardsLoading,
    error: rewardsError,
    getReferralCode,
    getReferralLink,
    claimReward
  } = useRewards();
  
  const {
    referrals,
    isLoading: referralsLoading,
    error: referralsError
  } = useReferral();
  
  const isLoading = rewardsLoading || referralsLoading;
  const error = rewardsError || referralsError;

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
    </div>;
  }

  if (error) {
    return <div className="flex items-center justify-center min-h-screen text-red-500">
      {error}
    </div>;
  }

  const referralCode = getReferralCode();
  const referralLink = getReferralLink();
  const totalGems = rewardsAccount?.points || 0;
  const shareContent = async (text: string, title: string = 'Share Referral') => {
    // Check if Web Share API is available
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: text,
        });
        track('referral_shared', { method: 'native_share' });
        showSuccessToast({
          title: 'Shared successfully!',
          message: 'Your referral has been shared.',
        });
      } catch (err) {
        // User cancelled or share failed, fallback to clipboard
        if ((err as Error).name !== 'AbortError') {
          await copyToClipboard(text);
        }
      }
    } else {
      // Web Share API not available, use clipboard
      await copyToClipboard(text);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      track('referral_shared', { method: 'clipboard' });
      showSuccessToast({
        title: 'Copied to clipboard!',
        message: 'Copied to clipboard! You can now share this referral code with your friends.',
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      showErrorToast({
        title: 'Failed to copy referral code',
        message: 'Please try again.',
      });
    }
  };
  const downloadQRCode = () => {
    if (!qrCodeRef.current) return;
    // Get the SVG element
    const svgElement = qrCodeRef.current.querySelector('svg');
    if (!svgElement) return;
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Set canvas dimensions
    canvas.width = 1000;
    canvas.height = 1000;
    // Create an image from the SVG
    const img = new Image();
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], {
      type: 'image/svg+xml;charset=utf-8'
    });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      // Draw white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Draw the image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      // Convert to PNG and download
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${referralCode}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      track('referral_shared', { method: 'qr_download' });
      // Clean up
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'transactions':
        return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <h3 className="text-lg font-medium mb-4 dark:text-white">
              Transaction History
            </h3>
            {transactions.length > 0 ? <div className="space-y-3">
                {transactions.map(item => {
                  const { label, tone } = getRewardTransactionDisplay(item.type);
                  const toneStyles = TRANSACTION_TONE_STYLES[tone];
                  const ToneIcon = TRANSACTION_TONE_ICONS[tone];
                  return <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-start space-x-2">
                        <div className={`p-1.5 rounded-full flex-shrink-0 ${toneStyles.wrapper}`}>
                          <ToneIcon className={`h-4 w-4 ${toneStyles.icon}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium dark:text-white text-sm truncate">
                            {label}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(item.createdOn || '').toLocaleDateString()}
                          </p>
                          {item.courierPackage && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {item.courierPackage.trackingNo}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`font-medium ${
                          item.points && item.points > 0 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        } text-sm`}>
                          {item.points}
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          GEMS
                        </p>
                      </div>
                    </div>
                  </div>;
                })}
              </div> : <p className="text-center py-6 text-gray-500 dark:text-gray-400">
                No transactions yet
              </p>}
          </div>;
      case 'claims':
        return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <h3 className="text-lg font-medium mb-4 dark:text-white">
              Claims History
            </h3>
            {claims.length > 0 ? <div className="space-y-3">
                {claims.map((item: RewardsClaim) => <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-start space-x-3">
                        <div className="bg-yellow-100 dark:bg-yellow-900/30 p-2 rounded-full">
                          <Award className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div>
                          <h4 className="font-medium dark:text-white">
                            {item.rewardPrize?.name}
                          </h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(item.lastUpdated || '').toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex items-center">
                        <Gift className="h-4 w-4 text-primary dark:text-primary mr-1" />
                        <span className="text-sm text-primary dark:text-primary">
                          {item.rewardPrize?.pointsRequired} gems
                        </span>
                      </div>
                      <span className={`inline-block px-2 py-1 ${
                        item.status === 'Pending' 
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          : item.status === 'Approved'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      } text-xs rounded-full`}>
                        {item.status}
                      </span>
                    </div>
                  </div>)}
              </div> : <p className="text-center py-6 text-gray-500 dark:text-gray-400">
                No claims yet
              </p>}
          </div>;
      case 'referrals':
        return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <h3 className="text-lg font-medium mb-4 dark:text-white">
              Referral History
            </h3>
            <div className="space-y-4">
              {referrals.map((referral) => (
                <div key={referral.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-full">
                        <User className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h4 className="font-medium dark:text-white">
                          {referral.referee?.firstName} {referral.referee?.lastName}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {referral.referee?.email}
                        </p>
                        <div className="mt-1 flex items-center flex-wrap gap-2">
                          {referral.signUpConfirmedOn && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full">
                              Signed Up
                            </span>
                          )}
                          {referral.emailConfirmedOn && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-full">
                              Email Verified
                            </span>
                          )}
                          {referral.phoneVerifiedOn && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
                              Phone Verified
                            </span>
                          )}
                          {referral.firstShipmentConfirmedOn && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                              First Shipment
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Joined {new Date(referral.signUpConfirmedOn || '').toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {referrals.length === 0 && (
                <p className="text-center py-6 text-gray-500 dark:text-gray-400">
                  No referrals yet. Share your referral code to start earning rewards!
                </p>
              )}
            </div>
          </div>;
      default:
        // 'earn' tab
        return <>
            {/* Gem Balance - Moved above Referral Program Card */}
            <div className="bg-primary text-white rounded-lg p-6 mb-4 text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-white/40 rounded-full p-4">
                  <Gift className="h-8 w-8" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-1">{totalGems}</h2>
              <p className="uppercase text-sm tracking-wider">YOUR GEMS</p>
            </div>
            {/* Redeem Reward Button */}
            <Button 
              onClick={() => setShowRewardsModal(true)} 
              variant="secondary"
              className="w-full text-primary font-medium py-4 px-4 rounded-lg shadow-md mb-6 flex items-center justify-center"
            >
              <Award className="h-5 w-5 mr-2" />
              REDEEM YOUR GEMS NOW
            </Button>
            {/* Referral Program Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center mb-4">
                <Gift className="h-5 w-5 text-primary dark:text-primary mr-2" />
                <h2 className="text-lg font-medium dark:text-white">
                  Your Referral Program
                </h2>
              </div>
              <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                <p className="text-primary">
                  Invite friends to join and earn {rewardSetting?.recruitedPoints} Gems for each successful referral. Your friends will also receive {rewardSetting?.recruitedPoints ? rewardSetting?.recruitedPoints / 2 : 0} Gems when they sign up!
                </p>
              </div>
              {/* QR Code Section */}
              <div className="mb-6 flex flex-col items-center">
                <div ref={qrCodeRef} className="bg-white p-4 rounded-lg mb-3 inline-block">
                  <QRCodeSVG value={referralLink} size={200} bgColor={'#FFFFFF'} fgColor={'#000000'} level={'H'} includeMargin={true} />
                </div>
                <button onClick={downloadQRCode} className="flex items-center text-primary dark:text-primary hover:text-blue-700 dark:hover:text-blue-300">
                  <Download className="h-4 w-4 mr-1" />
                  Download QR Code
                </button>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Your Referral Code
                </label>
                <div className="flex">
                  <input type="text" value={referralCode} readOnly className="flex-1 border border-gray-300 dark:border-gray-600 rounded-l-md px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white" />
                  <button onClick={() => shareContent(referralCode, 'Share Referral Code')} className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-md px-3 py-2 flex items-center">
                    <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Referral Link
                </label>
                <div className="flex">
                  <input type="text" value={referralLink} readOnly className="flex-1 border border-gray-300 dark:border-gray-600 rounded-l-md px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                  <button onClick={() => shareContent(referralLink, 'Share Referral Link')} className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-md px-3 py-2 flex items-center">
                    <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
            {/* How to Earn Gems */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center justify-center mb-4">
                <Gift className="h-5 w-5 text-primary dark:text-primary mr-2" />
                <h2 className="text-lg font-medium dark:text-white">
                  How to Earn Gems
                </h2>
              </div>
              <div className="space-y-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex justify-between items-center">
                  <div className="flex items-start">
                    <Users className="h-5 w-5 text-primary dark:text-primary mr-3 mt-1" />
                    <div>
                      <h3 className="font-medium dark:text-white">
                        Recruit a friend
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Earn points when your friend:
                      </p>
                      <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc ml-5 mt-1">
                        <li>Signs up and verifies email ({rewardSetting?.recruitedPoints} gems)</li>
                        <li>Verifies phone number ({rewardSetting?.phoneVerifiedPoints} gems)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary dark:text-primary">
                      {(rewardSetting?.recruitedPoints || 0) + (rewardSetting?.phoneVerifiedPoints || 0)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      TOTAL GEMS
                    </p>
                  </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex justify-between items-center">
                  <div className="flex items-start">
                    <Package className="h-5 w-5 text-primary dark:text-primary mr-3 mt-1" />
                    <div>
                      <h3 className="font-medium dark:text-white">
                        Friend ships first package
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        When they make their first shipment
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary dark:text-primary">
                      {rewardSetting?.shippedPoints}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      GEMS
                    </p>
                  </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex justify-between items-center">
                  <div className="flex items-start">
                    <ShoppingBag className="h-5 w-5 text-primary dark:text-primary mr-3 mt-1" />
                    <div>
                      <h3 className="font-medium dark:text-white">
                        Ship a package
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Every time you ship
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary dark:text-primary">
                      {rewardSetting?.packagePoints}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      GEMS
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </>;
    }
  };
  return <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pb-20 md:pb-6">
      <div className="max-w-4xl mx-auto">
        {/* Mobile-Optimized Tab Navigation */}
        <div className="mb-6 px-4 pt-6">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setActiveTab('earn')} className={`px-2 py-3 rounded-lg font-medium text-center ${activeTab === 'earn' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
              Earn & Redeem
            </button>
            <button onClick={() => setActiveTab('transactions')} className={`px-2 py-3 rounded-lg font-medium text-center ${activeTab === 'transactions' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
              Transactions
            </button>
            <button onClick={() => setActiveTab('claims')} className={`px-2 py-3 rounded-lg font-medium text-center ${activeTab === 'claims' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
              Claims
            </button>
            <button onClick={() => setActiveTab('referrals')} className={`px-2 py-3 rounded-lg font-medium text-center ${activeTab === 'referrals' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
              Referrals
            </button>
          </div>
        </div>
        {/* Tab Content */}
        <div className="px-4 md:px-6">{renderTabContent()}</div>
        {/* Rewards Modal */}
        {showRewardsModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-primary to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white p-6 rounded-t-lg">
                <h2 className="text-xl font-bold mb-1 text-center">
                  Available Rewards
                </h2>
                <p className="text-center text-blue-100">
                  Redeem your gems for amazing rewards
                </p>
                <div className="flex items-center justify-center mt-3">
                  <Gift className="h-5 w-5 mr-2" />
                  <span className="font-medium">
                    {totalGems} gems available
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {availableRewards.map(reward => <div key={reward.id} className="border dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          <div className="bg-yellow-100 dark:bg-yellow-900/30 p-2 rounded-full mr-3">
                            <Award className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                          </div>
                          <div>
                            <h3 className="font-medium dark:text-white">
                              {reward.name}
                            </h3>
                            {reward.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {reward.description}
                              </p>
                            )}
                            <div className="flex items-center mt-1">
                              <Gift className="h-4 w-4 text-primary dark:text-primary mr-1" />
                              <span className="text-sm text-primary dark:text-primary">
                                {reward.pointsRequired}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button 
                          onClick={async () => {
                            try {
                              const success = await claimReward(reward.id || 0);
                              if (success) {
                                track('reward_claimed', {
                                  reward_id: reward.id,
                                  prize: reward.name,
                                  points: reward.pointsRequired,
                                });
                                setShowRewardsModal(false);
                              }
                            } catch {
                              // useRewards has already shown the server's reason.
                            }
                          }}
                          disabled={!rewardsAccount || totalGems < (reward.pointsRequired || 0)} 
                          className={`px-3 py-1.5 rounded text-sm font-medium ${totalGems >= (reward.pointsRequired || 0) ? 'bg-primary dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'}`}
                        >
                          {totalGems >= (reward.pointsRequired || 0) ? 'Claim' : 'Not Enough'}
                        </button>
                      </div>
                    </div>)}
                </div>
                <button onClick={() => setShowRewardsModal(false)} className="w-full mt-6 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded">
                  Close
                </button>
              </div>
            </div>
          </div>}
      </div>
    </div>;
};
export default Referrals;